import React from 'react';
import { Table, Button } from '@crisellevaldez/speculo-ui';
import { useToast } from '@crisellevaldez/speculo-ui';
import { HiHome } from 'react-icons/hi';
import { formatDate } from '../../../../utils/dateTime';
import { formatCurrency } from '../../../../utils/formatters';
import { TablePagination } from '../../../common/TablePagination';
import { TableCard } from '../../../common/TableCard';
import { ActivityTableHeader } from './ActivityTableHeader';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { useTenant } from '../../../../hooks/client/tenant/useTenant';
import { useContacts } from '../../../../hooks/useContacts';
import type { useContactValuationsGQL } from '../../../../hooks/graphql/contacts/useContactValuationsGQL';
import type { GQLValuationItem } from '../../../../hooks/graphql/contacts/useContact';
import { isPreviewContactId, PREVIEW_VALUATIONS } from '../../../../pages/previewMockData';

/** The address shape persisted on the contact, as `Contact.address` holds it. */
export interface AssignedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface HomeValuationsTabProps {
  contactId: string;
  valuations: ReturnType<typeof useContactValuationsGQL>;
  firebaseUid?: string;
  isActive?: boolean;
  /**
   * Called with the address that was just saved to the contact. The owner
   * applies it to its own contact snapshot — a GET_CONTACT refetch cannot do
   * that job here, because `fetchAndSetContact` re-applies the contact prop it
   * captured and would clobber the save with pre-update data.
   */
  onAddressAssigned?: (address: AssignedAddress) => void;
}

interface AddressObj {
  address: string;
  city: string;
  state: string;
  zip: string;
}

const getColumns = (
  websiteDomain: string,
  onAssignAddress: (valuationId: string, address: AddressObj) => void,
  assigningId: string | null
) => [
  {
    header: 'Property',
    key: 'property',
    width: '450px',
    cell: (item: GQLValuationItem) => {
      const fullAddress = item.fullAddress || item.addressStreet || 'Unknown Address';
      const city = item.addressCity || '';
      const state = item.addressState || '';
      const zip = item.addressZip || '';
      const propertyUrl =
        websiteDomain && item.valuationId
          ? `https://${websiteDomain}/property/${item.valuationId}/valuation`
          : '';
      const addressObj = { address: item.addressStreet || '', city, state, zip };
      return (
        <div className="flex items-start gap-3">
          <div className="w-16 h-16 bg-muted rounded-md flex items-center justify-center">
            <HiHome className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="flex-grow">
            {propertyUrl ? (
              <a
                href={propertyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
                title={fullAddress}
              >
                {fullAddress}
              </a>
            ) : (
              <div className="font-medium">{fullAddress}</div>
            )}
            <div className="flex items-center justify-between gap-2">
              <div className="flex-grow">
                <div className="text-sm text-muted-foreground">
                  {city && state && `${city}, ${state} ${zip}`}
                </div>
                {item.bedrooms != null && (
                  <div className="text-sm text-muted-foreground">
                    {item.bedrooms || 0} bed • {item.bathrooms || 0} bath
                    {item.squareFeet != null &&
                      item.squareFeet > 0 &&
                      ` • ${item.squareFeet.toLocaleString()} sqft`}
                  </div>
                )}
              </div>
              <div className="flex-shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onAssignAddress(item.valuationId, addressObj)}
                  // Only one assignment at a time: the rest go flat so a second
                  // click can't race the first onto the same contact.
                  disabled={
                    !item.addressStreet ||
                    (assigningId !== null && assigningId !== item.valuationId)
                  }
                  isLoading={assigningId === item.valuationId}
                  className="text-xs whitespace-nowrap"
                >
                  Use Address
                </Button>
              </div>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    header: 'Estimated Value',
    key: 'estimatedValue',
    width: '140px',
    cell: (item: GQLValuationItem) => (
      <div className="font-medium">
        {item.estimatedValue ? formatCurrency(item.estimatedValue) : 'N/A'}
      </div>
    ),
  },
  {
    header: 'Lot Size',
    key: 'lotSize',
    width: '120px',
    cell: (item: GQLValuationItem) => {
      const lotSize = item.lotSize;
      return <div className="text-sm">{lotSize ? `${lotSize.toLocaleString()} sqft` : 'N/A'}</div>;
    },
  },
  {
    header: 'Created At',
    key: 'createdAt',
    width: '180px',
    cell: (item: GQLValuationItem) => {
      const { organization } = useOrganizationStore.getState();
      return formatDate(item.createdAt ?? '', organization?.timezone);
    },
  },
];

export const HomeValuationsTab: React.FC<HomeValuationsTabProps> = ({
  contactId,
  valuations,
  onAddressAssigned,
}) => {
  const { showToast } = useToast();
  const { editContact } = useContacts();
  const { organization } = useOrganizationStore();
  const { data: tenant } = useTenant(organization?.id);
  const websiteDomain = tenant?.domain?.primary || '';

  const isPreview = isPreviewContactId(contactId);
  const items: GQLValuationItem[] = isPreview ? PREVIEW_VALUATIONS : valuations.items;
  const total = isPreview ? PREVIEW_VALUATIONS.length : valuations.total;
  const hasNext = isPreview ? false : valuations.hasNext;
  const loading = isPreview ? false : valuations.loading;
  const handleNextPage = valuations.handleNextPage;

  // Which row's assignment is in flight, for that row's spinner. The ref is the
  // actual guard: state reads inside the handler would be a render behind.
  const [assigningId, setAssigningId] = React.useState<string | null>(null);
  const inFlightRef = React.useRef(false);

  const handleAssignAddress = React.useCallback(
    async (valuationId: string, address: AddressObj) => {
      if (!address || !contactId || inFlightRef.current) return;
      inFlightRef.current = true;
      setAssigningId(valuationId);
      const saved: AssignedAddress = {
        street: address.address || '',
        city: address.city || '',
        state: address.state || '',
        zip: address.zip || '',
      };
      try {
        await editContact(contactId, {
          address: saved,
        } as Parameters<typeof editContact>[1]);
        onAddressAssigned?.(saved);
        showToast({ message: 'Address assigned to contact successfully', variant: 'success' });
      } catch {
        // Without this the rejection surfaced only as an unhandled promise and
        // the row sat there looking like the save had worked.
        showToast({
          message: 'Could not assign the address to this contact. Please try again.',
          variant: 'error',
        });
      } finally {
        inFlightRef.current = false;
        setAssigningId(null);
      }
    },
    [contactId, editContact, showToast, onAddressAssigned]
  );

  const columns = React.useMemo(
    () => getColumns(websiteDomain, handleAssignAddress, assigningId),
    [websiteDomain, handleAssignAddress, assigningId]
  );

  return (
    <div className="flex flex-col gap-4">
      <ActivityTableHeader
        title="Home Valuations"
        count={total}
        isLoading={loading}
        showDateRange={false}
      />

      <TableCard
        borderless
        className="flex-shrink-0"
        bodyClassName="min-h-[200px] max-h-[640px] overflow-y-auto scrollbar-custom"
        footer={
          hasNext ? (
            <TablePagination hasNext={hasNext} onNextPage={handleNextPage} isLoading={loading} />
          ) : null
        }
      >
        <Table
          data={items}
          columns={columns}
          keyExtractor={(item) => item.valuationId}
          emptyStateText="No home valuations available"
          size="xl"
          loading={loading && items.length === 0}
        />
      </TableCard>
    </div>
  );
};
